import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

/**
 * SourceSplit — «источник задания» рядом с вопросами.
 *
 * ЗАЧЕМ ОБЩИЙ. Такая раскладка нужна пяти экранам: чтение, пробный экзамен,
 * спринт, тренажёр, аудирование. Поведение было написано один раз — в чтении, —
 * а остальные четыре сваливали текст и вопросы одной лентой: ученик с телефона
 * листал вверх к тексту и обратно на каждый вопрос. На экзамене с таймером это
 * прямо съедает время.
 *
 * Копировать разметку в четыре места было нельзя: копии расходятся. Поэтому
 * поведение ИЗВЛЕЧЕНО из чтения (то есть проверенное, а не написанное заново) и
 * подключается всюду.
 *
 * ДВА РЕЖИМА, и разница между ними принципиальна:
 *
 *   text  — источник читают. На телефоне вкладки «Текст / Вопросы»: каждый
 *           экран занят одним делом.
 *
 *   audio — источник СЛУШАЮТ. Вкладки здесь были бы вредны: спрятав плеер,
 *           мы отняли бы у ученика паузу и перемотку — чтобы нажать паузу,
 *           пришлось бы переключить вкладку. Поэтому плеер ЗАКРЕПЛЁН сверху и
 *           виден всегда, а вопросы прокручиваются под ним.
 *
 * На широком экране в обоих режимах — две колонки с перетаскиваемой границей,
 * как на настоящем компьютерном IELTS. Источник в левой колонке ЗАКРЕПЛЁН:
 * прокручиваются только вопросы, а запись или отрывок остаются на месте. Когда
 * вопросы блока заканчиваются, источник уходит вместе с блоком и на его место
 * встаёт следующий — ровно так же ведёт себя настоящий экзамен, где часть
 * заданий привязана к одной записи.
 */
export type SourceSplitProps = {
  /** Источник: текст задания или плеер с записью. */
  source: ReactNode;
  /** Вопросы. */
  questions: ReactNode;
  /**
   * Что за источник. От этого зависит поведение на телефоне — см. описание
   * компонента. По умолчанию текст.
   */
  kind?: 'text' | 'audio';
  /** Подпись вкладки источника. По умолчанию «Текст». */
  sourceLabel?: string;
  /** Ограничение высоты колонок на широком экране. */
  paneClassName?: string;
  className?: string;
  /**
   * Какая вкладка открыта — если страница хочет управлять этим сама.
   *
   * Нужно там, где переключение вызывает не только нажатие на вкладку: в
   * чтении клик по номеру вопроса должен САМ перевести на панель вопросов,
   * иначе ученик нажимает на вопрос и остаётся смотреть в текст.
   * Без этих двух свойств компонент управляет вкладкой сам.
   */
  pane?: 'source' | 'questions';
  onPaneChange?: (pane: 'source' | 'questions') => void;
  /** Показывать ли вкладки. По умолчанию — на узком экране для текста. */
  hideTabs?: boolean;
};

/** Ширина, с которой две колонки перестают быть теснотой. */
const WIDE_QUERY = '(min-width: 1024px)';

export function SourceSplit({
  source,
  questions,
  kind = 'text',
  sourceLabel,
  paneClassName,
  className,
  pane,
  onPaneChange,
  hideTabs,
}: SourceSplitProps) {
  const { t } = useTranslation();
  const splitRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const [leftPct, setLeftPct] = useState(50);
  const [wide, setWide] = useState(false);
  const [ownTab, setOwnTab] = useState<'source' | 'questions'>('source');
  // Управляемый режим: если страница передала pane, слушаемся её.
  const tab = pane ?? ownTab;
  const setTab = (v: 'source' | 'questions') => {
    setOwnTab(v);
    onPaneChange?.(v);
  };

  useEffect(() => {
    const mq = window.matchMedia(WIDE_QUERY);
    const sync = () => setWide(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  // Перетаскивание границы. Границы 28–72% не дают схлопнуть колонку в ноль,
  // из которого её потом не вытащить мышью.
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current || !splitRef.current) return;
      const rect = splitRef.current.getBoundingClientRect();
      setLeftPct(Math.min(72, Math.max(28, ((e.clientX - rect.left) / rect.width) * 100)));
    };
    const onUp = () => {
      dragging.current = false;
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  // Вкладки только для читаемого источника и только на узком экране.
  const showTabs = kind === 'text' && !wide && !hideTabs;
  const hideSource = showTabs && tab !== 'source';
  const hideQuestions = showTabs && tab !== 'questions';

  return (
    <div className={className}>
      {showTabs && (
        <div className="mb-3 flex gap-1 rounded-pill bg-surface-container-low p-1">
          {(['source', 'questions'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setTab(v)}
              className={cn(
                'flex-1 rounded-pill px-3 py-1.5 text-xs font-semibold uppercase tracking-label transition',
                tab === v
                  ? 'bg-primary text-on-primary'
                  : 'text-on-surface-variant hover:bg-surface-container',
              )}
            >
              {v === 'source'
                ? sourceLabel || t('ielts.reading.paneText')
                : t('ielts.reading.paneQuestions')}
            </button>
          ))}
        </div>
      )}

      <div
        ref={splitRef}
        className="flex min-h-0 flex-col gap-4 lg:flex-row lg:gap-0"
      >
        <div
          className={cn(
            'min-w-0',
            // Запись остаётся на виду при прокрутке вопросов: пауза и перемотка
            // должны быть под рукой в любой момент.
            kind === 'audio' && !wide && 'sticky top-0 z-20 -mx-1 bg-background/95 px-1 pb-2 pt-1 backdrop-blur',
            // На широком экране источник ЗАКРЕПЛЯЕТСЯ так же.
            //
            // Раньше закрепление работало только на телефоне, а на компьютере
            // обе колонки уезжали вместе со страницей: ученик листал вопросы и
            // терял из виду плеер, а в чтении — сам отрывок. Приходилось гонять
            // страницу вверх-вниз на каждый вопрос, и это на экзамене с таймером.
            //
            // `self-start` здесь обязателен: колонки лежат во flex-контейнере,
            // где по умолчанию элемент растянут во всю высоту блока — а
            // растянутому элементу закрепление ничего не даёт, ему просто некуда
            // «прилипать». Именно поэтому прежняя попытка и не сработала.
            wide && 'sticky top-[72px] max-h-[calc(100vh-96px)] self-start overflow-y-auto',
            hideSource && 'hidden',
          )}
          style={wide ? { width: `${leftPct}%` } : undefined}
        >
          <div className={wide ? paneClassName : undefined}>{source}</div>
        </div>

        {/* Граница между колонками. Только для мыши — на телефоне колонок нет. */}
        <div
          onMouseDown={() => {
            dragging.current = true;
            document.body.style.userSelect = 'none';
          }}
          className="hidden shrink-0 cursor-col-resize items-center px-1.5 lg:flex"
          title={t('ielts.reading.dragResize')}
        >
          <span className="flex h-16 w-2.5 items-center justify-center rounded-pill bg-surface-container-high text-on-surface-variant transition hover:bg-primary-container">
            <span className="material-symbols-outlined text-sm">drag_indicator</span>
          </span>
        </div>

        <div
          className={cn('min-w-0', hideQuestions && 'hidden')}
          style={wide ? { width: `${100 - leftPct}%` } : undefined}
        >
          <div className={wide ? paneClassName : undefined}>{questions}</div>
        </div>
      </div>
    </div>
  );
}
